/**
 * Week math for the meal planner.
 *
 * Dates are 'YYYY-MM-DD' strings and all arithmetic is done in UTC so the answer
 * never shifts by a day depending on the time of evening the app is opened.
 * "Today" is read from the local clock (the kitchen's clock, not UTC's).
 */

const DAY_MS = 86_400_000;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function toMs(iso: string): number {
  return Date.parse(`${iso}T00:00:00Z`);
}

function fromMs(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** Monday of the week containing `iso`. Falls back to this week if `iso` is junk. */
export function mondayOf(iso: string): string {
  const ms = toMs(iso);
  if (Number.isNaN(ms)) return mondayOf(todayIso());
  const dow = (new Date(ms).getUTCDay() + 6) % 7; // Mon = 0
  return fromMs(ms - dow * DAY_MS);
}

export function addDays(iso: string, n: number): string {
  return fromMs(toMs(iso) + n * DAY_MS);
}

export function weekDays(weekStart: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
}

/** Noon avoids any DST edge when formatting for display. */
function local(iso: string): Date {
  return new Date(`${iso}T12:00:00`);
}

export function weekdayName(iso: string, long = false): string {
  return local(iso).toLocaleDateString('en-US', { weekday: long ? 'long' : 'short' });
}

export function monthDay(iso: string): string {
  return local(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** "Jul 7 – 13" / "Jun 30 – Jul 6" */
export function weekLabel(weekStart: string): string {
  const end = addDays(weekStart, 6);
  const sameMonth = weekStart.slice(0, 7) === end.slice(0, 7);
  return `${monthDay(weekStart)} – ${sameMonth ? String(Number(end.slice(8, 10))) : monthDay(end)}`;
}

export function isThisWeek(weekStart: string): boolean {
  return weekStart === mondayOf(todayIso());
}
